import { css } from 'lit';
import { ValidationResult } from 'cra-web-components/components/form-base/cra-form-field';
import { CRAFormController, FormBaseData, FormField } from 'cra-web-components';
import { CSSResultArray, LitElement, TemplateResult, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { Ref, createRef, ref } from 'lit/directives/ref.js';
import store from '../../redux/redux-store';
import resetScss from '../css/lib/reset.scss';
import sharedScss from '../css/shared.scss';
import scss from '../../utils/scssAsCss';
import '../common/password-input';
import '../common/notification-banner';
import utilityClassesCss from '../css/utilityClasses.css';
import remEventFormStyles from '../rem/rem-event-form-styles';
import dialogCss from '../css/dialog.css';
import { userSelector } from '../../redux/user-management/user-management-selectors';
import { EditUserDto } from '../../../typings/api';
// import { editUsersPassword } from '../../redux/user-management/user-management-actions';
import { showMainBanner } from '../../redux/redux-ui';
import { NotificationErrorType } from '../../../typings/shared-types';
import { PASSWORD_REQUIREMENTS } from '../../config/ConfigLogin';

enum fieldNames {
	password = 'password',
	passwordConfirm = 'passwordConfirm',
}

interface PassResetForm extends FormBaseData<fieldNames> {
	password: FormField<string, fieldNames>;
	passwordConfirm: FormField<string, fieldNames>;
}

export type PassResetFormValues = { [P in keyof PassResetForm]?: PassResetForm[P]['value'] };

@customElement('password-change-dialog')
export default class PasswordChangeDialog extends LitElement {
	private dialogRef: Ref<HTMLDialogElement> = createRef();

	@state() userId?: number;

	user?: EditUserDto;

	@state() isLoading = false;

	expectUser = true;

	allowBackdropClose = true;

	@state() isPasswordShown = false;

	public static override get styles(): CSSResultArray {
		return [
			resetScss,
			sharedScss,
			utilityClassesCss,
			remEventFormStyles,
			dialogCss,
			css`
.pb-2 {
  padding-bottom: calc(var(--ggutter) * 0.5);
}

.pb-3 {
  padding-bottom: var(--ggutter);
}

.grid-2-col {
  display: grid;
  grid-template-columns: 1fr;
  column-gap: var(--ggutter);
}
@media (min-width: 850px) {
  .grid-2-col {
    grid-template-columns: 1fr 1fr;
  }
}

.grid-2-col-row-gap {
  row-gap: var(--ggutter);
}

@media (min-width: 850px) {
  .dialog-form-pass-change {
    width: 700px;
  }
}
@media (min-width: 1024px) {
  .dialog-form-pass-change {
    width: 800px;
  }
}

.color-brand-dark {
  color: rgb(var(--brand-grey-dark));
}

.list {
  list-style: disc;
  display: grid;
  white-space: nowrap;
  grid-template-columns: 1fr;
  column-gap: var(--ggutter);
}
@media (min-width: 850px) {
  .list {
    grid-template-columns: min-content min-content;
  }
}
.list > li {
  margin-left: calc(var(--ggutter));
}
`,
		];
	}

	generateFormData = (values?: PassResetFormValues) => {
		return {
			password: new FormField(values?.password ?? '', {
				validateField: (val: string): ValidationResult => {
					if (!val || val.length === 0) {
						return {
							isError: true,
							errorMessage: 'Password is required',
						};
					}

					for (const requirement of PASSWORD_REQUIREMENTS) {
						if (!requirement.validate(val)) {
							return {
								isError: true,
								errorMessage: requirement.errorMessage,
							};
						}
					}

					const username = this.user?.username;
					const emailPart = this.user?.email?.split('@')[0];

					if ((username && val.includes(username)) || (emailPart && val.includes(emailPart))) {
						return {
							isError: true,
							errorMessage: 'Password cannot contain username or email',
						};
					}

					return { isError: false, errorMessage: '' };
				},
			}),
			passwordConfirm: new FormField(values?.passwordConfirm ?? '', {
				validateField: (val: string, form?: FormBaseData<fieldNames>): ValidationResult => {
					if (val !== form?.password.value) {
						return {
							isError: true,
							errorMessage: 'Password Confirm must match Password',
						};
					}
					return { isError: false, errorMessage: '' };
				},
			}),
		};
	};

	form!: CRAFormController<fieldNames, PassResetForm>;

	public override render(): TemplateResult {
		const forUsername = this.user?.username
			? html`for <i class="dialog-i">${this.user.username}</i>`
			: undefined;
		return html`
			<dialog ${ref(this.dialogRef)} class="dialog" @click=${this.onBackdropClose}>
				<form @submit=${this.onSubmit} class="dialog-form dialog-form-pass-change">
					<h2 class="dialog-h2 pb-3">Reset Password ${forUsername}</h2>

					<div class="grid-2-col pb-3 grid-2-col-row-gap">
						<label class="field-label flex col">
							<div class="flex">Password</div>

							<password-input
								name=${fieldNames.password}
								.value=${this.form.data[fieldNames.password].value}
								@input=${this.form.handleTextInput}
								@blur=${this.handlePassValidate}
								@cra:password:visibility=${this.handlePasswordVisibilityChanged}
								?isPasswordShown=${this.isPasswordShown}
								?isError=${this.form.data[fieldNames.password].isError}
								errorMessage=${this.form.data[fieldNames.password].errorMessage}
							>
							</password-input>
						</label>
						<label class="field-label flex col">
							<p>Password Confirm</p>
							<password-input
								name=${fieldNames.passwordConfirm}
								.value=${this.form.data[fieldNames.passwordConfirm].value}
								@input=${this.form.handleTextInput}
								@blur=${this.handlePassConfirmValidate}
								@cra:password:visibility=${this.handlePasswordVisibilityChanged}
								?isPasswordShown=${this.isPasswordShown}
								?isError=${this.form.data[fieldNames.passwordConfirm].isError}
								errorMessage=${this.form.data[fieldNames.passwordConfirm].errorMessage}
							></password-input>
						</label>
					</div>

					<div class="dialog-font-size-sm color-brand-dark">
						<p>Password Must:</p>
						<ul class="list grid-2-col">
							${PASSWORD_REQUIREMENTS.map((req) => html`<li>${req.description}</li>`)}
						</ul>
					</div>

					<div class="flex justify-content-end dialog-pt-6">
						<button class="btn btn-lg" type="button" @click=${this.onClose}>Cancel</button>
						<button type="submit" class="btn btn-lg btn-primary" ?disabled=${this.isLoading}>
							Reset Password
						</button>
					</div>
				</form>
			</dialog>
		`;
	}

	handlePasswordVisibilityChanged(event: CustomEvent<boolean>) {
		this.isPasswordShown = event.detail;
	}

	handlePassValidate(event: InputEvent) {
		// custom validation for password because we need to revalidate password confirm
		this.form.updateValue(fieldNames.password, (event.target as HTMLInputElement).value);
		void this.form.validateField(fieldNames.password);
		if (this.form.data[fieldNames.passwordConfirm].value) {
			void this.form.validateField(fieldNames.passwordConfirm, true);
		}
	}

	handlePassConfirmValidate(event: InputEvent) {
		// custom validation for password confirm because we need to force revalidation
		this.form.updateValue(fieldNames.passwordConfirm, (event.target as HTMLInputElement).value);
		void this.form.validateField(fieldNames.passwordConfirm, true);
	}

	override connectedCallback(): void {
		super.connectedCallback();

		this.form = new CRAFormController<fieldNames, PassResetForm>(this, this.generateFormData);
		if (!this.expectUser) {
			return;
		}
		if (this.userId === undefined) {
			throw new Error('userId is required');
		}

		this.user = userSelector(store.getState(), this.userId);
		if (!this.user) {
			store.dispatch(
				// User deleted somewhere else before dialog opened?
				showMainBanner(NotificationErrorType.ERROR, {
					title: `User with id ${this.userId} not found`,
				}),
			);
			this.#close();
		}
	}

	protected override firstUpdated(): void {
		this.dialogRef.value?.showModal(); // redirect focus
	}

	private onBackdropClose(event: MouseEvent): void {
		if (!this.allowBackdropClose) {
			return;
		}
		const dialog = this.dialogRef?.value as HTMLDialogElement;
		if (dialog !== undefined && event.target === dialog) {
			this.#close();
		}
	}

	private onClose(event: MouseEvent): void {
		event.preventDefault();
		this.#close();
	}

	private async onSubmit(event: MouseEvent) {
		event.preventDefault();

		if (await this.form.validateAllAndCheck()) {
			this.isLoading = true;
			const formData = this.form.getValues();
			this.dispatchEvent(
				new CustomEvent('submit', {
					composed: true,
					detail: {
						password: formData.password,
						userId: this.userId,
						closeFn: () => this.#close(true),
					},
				}),
			);
			// await store.dispatch(editUsersPassword(this.userId as number, formData.password as string));
			// this.#close(true);
		}
	}

	#close = (forceClose = false) => {
		const shouldAsk = this.form.differed() && !forceClose;
		const dialog = this.dialogRef?.value as HTMLDialogElement;

		// Always ask if password reset modal from link
		if (shouldAsk && window.confirm('Discard changes?')) {
			dialog.close();
			this.dispatchEvent(new CustomEvent('close', { composed: true }));
			this.isLoading = false;
		} else if (!shouldAsk) {
			dialog.close();
			this.dispatchEvent(new CustomEvent('close', { composed: true }));
			this.isLoading = false;
		}
	};
}
